import { motion } from "framer-motion";
import { Shield, Lock, Eye, FileCheck, AlertTriangle, CheckCircle2, Server, Key, Fingerprint, Scale, Globe, UserCheck } from "lucide-react";
import controlRoom from "../assets/control-room.jpg";
import Section from "../components/Section";
import SectionHeading from "../components/SectionHeading";
import PageHero from "../components/PageHero";
import EnergyParticles from "../components/EnergyParticles";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const SecurityCompliance = () => (
  <div>
    <PageHero
      eyebrow="Security & Compliance"
      title={<>Built with <span className="energy-gradient-text">Transparency & Protection</span></>}
      description="Your security is our top priority. AeroVolt implements enterprise-grade data protection, strict compliance standards, and transparent operational practices."
      backgroundImage={controlRoom}
    />

    {/* Core Security */}
    <Section>
      <SectionHeading eyebrow="Data Protection" title="Enterprise-Grade Security" description="Multiple layers of security protect your data, funds, and personal information at every level." />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          { icon: Lock, title: "256-Bit SSL Encryption", desc: "All data transmitted between your device and our servers is encrypted using industry-standard TLS 1.3 with 256-bit AES encryption — the same standard used by major financial institutions." },
          { icon: Server, title: "Secure Infrastructure", desc: "Our platform runs on enterprise cloud infrastructure with SOC 2 Type II compliance, redundant data centers, and 99.99% uptime guarantee across multiple geographic regions." },
          { icon: Key, title: "Two-Factor Authentication", desc: "Mandatory 2FA on all partner accounts using time-based one-time passwords (TOTP) or hardware security keys for an additional layer of access protection." },
          { icon: Fingerprint, title: "Biometric Verification", desc: "Support for fingerprint and facial recognition on mobile devices, providing convenient yet secure access to your dashboard and wallet." },
          { icon: Eye, title: "Real-Time Monitoring", desc: "24/7 automated threat detection and anomaly monitoring systems identify and neutralize potential security threats before they impact operations." },
          { icon: Shield, title: "Cold Storage", desc: "The majority of platform reserves are held in cold storage wallets with multi-signature access requirements, minimizing exposure to online threats." },
        ].map((s, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="p-6 rounded-2xl glass-card glass-card-hover group transition-all duration-500">
            <div className="w-12 h-12 rounded-xl energy-gradient flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_hsl(155_80%_50%/0.3)] transition-shadow">
              <s.icon className="w-6 h-6 text-primary-foreground" />
            </div>
            <h3 className="font-display text-base font-semibold mb-2">{s.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* KYC */}
    <Section className="bg-secondary/20 relative overflow-hidden">
      <EnergyParticles />
      <div className="relative z-10">
        <SectionHeading eyebrow="Verification" title="KYC & Identity Verification" description="All partners undergo a comprehensive Know Your Customer process to maintain platform integrity." />
        <div className="max-w-3xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { step: "01", title: "Identity Verification", desc: "Government-issued photo ID verification using AI-powered document authentication. Supports passports, driving licences, and national ID cards from 190+ countries." },
              { step: "02", title: "Address Verification", desc: "Proof of address through utility bills, bank statements, or government correspondence dated within the last 3 months." },
              { step: "03", title: "AML Screening", desc: "Automated Anti-Money Laundering checks against global watchlists, sanctions databases, and Politically Exposed Persons (PEP) registries." },
              { step: "04", title: "Ongoing Monitoring", desc: "Continuous transaction monitoring and periodic re-verification to maintain compliance with evolving regulatory requirements." },
            ].map((k, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="p-5 rounded-2xl glass-card glass-card-hover group transition-all duration-500">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-8 h-8 rounded-lg energy-gradient flex items-center justify-center font-display text-xs font-bold text-primary-foreground">{k.step}</span>
                  <h3 className="font-display text-sm font-semibold">{k.title}</h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{k.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </Section>

    {/* Regulatory */}
    <Section>
      <SectionHeading eyebrow="Regulatory Framework" title="Compliance & Oversight" description="AeroVolt operates within established legal and regulatory frameworks." />
      <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-10">
        {[
          { icon: Scale, title: "Financial Conduct Authority (FCA)", desc: "Financial promotions and activities are subject to oversight by the UK Financial Conduct Authority. AeroVolt maintains compliance with FCA guidelines for financial promotions and consumer communications." },
          { icon: Globe, title: "GDPR Compliance", desc: "Full compliance with the General Data Protection Regulation (EU) and UK Data Protection Act 2018, ensuring your personal data is processed lawfully, fairly, and transparently." },
          { icon: FileCheck, title: "Anti-Money Laundering", desc: "Comprehensive AML policies aligned with the Money Laundering, Terrorist Financing and Transfer of Funds Regulations 2017 (UK) and equivalent international standards." },
          { icon: UserCheck, title: "Consumer Protection", desc: "Clear risk disclosures, fair terms of service, and accessible complaint procedures in line with FCA Consumer Duty requirements." },
        ].map((r, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="p-6 rounded-2xl glass-card glass-card-hover group transition-all duration-500">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <r.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-display text-sm font-semibold">{r.title}</h3>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{r.desc}</p>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Risk Disclosure Banner */}
    <Section className="bg-secondary/20">
      <motion.div {...fadeUp} className="max-w-3xl mx-auto p-8 rounded-2xl border border-accent/30 bg-accent/5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-accent" />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold mb-3">Important Risk Disclosure</h3>
            <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
              <p>Participation in renewable energy projects involves inherent risks including, but not limited to, market fluctuations, weather variability, operational challenges, and regulatory changes.</p>
              <p><strong className="text-foreground">No Guaranteed Returns:</strong> All distributions are performance-based and depend on actual energy production and revenue. Past performance does not guarantee future results. Income is not guaranteed.</p>
              <p><strong className="text-foreground">Capital Risk:</strong> Invested capital may be at risk. Partners should only participate with funds they can afford to commit for the full term of their selected plan.</p>
              <p>AeroVolt strongly recommends that all potential partners seek independent financial advice before making any participation decisions.</p>
            </div>
          </div>
        </div>
      </motion.div>
    </Section>
  </div>
);

export default SecurityCompliance;
